// Iron Command — Auto-General sim. Pure, deterministic, no DOM. Both sides play by the same rules.
export const W = 9, H = 16, LANES = [1.5, 4.5, 7.5], MID = H / 2;
export const DT = 0.1;
export const HQ_HP = 2200;
// Overtime: after 2:30 every hit grows stronger so matches land around 3-4 minutes.
export const OVERTIME = 135;
export const dmgScale = (t) => (t <= OVERTIME ? 1 : 1 + (t - OVERTIME) / 45);
export const HQ = { 0: { x: 4.5, y: H - 1.3 }, 1: { x: 4.5, y: 1.3 } };

// Counter table: attacker class -> target class multiplier.
export const UNITS = {
  infantry:  { name: "Rifles",    icon: "🪖", cost: 60,  hp: 130, dmg: 14, range: 1.4, rate: 0.8, speed: 0.95, cls: "soft",  needs: "barracks", count: 3, vs: { soft: 1.0, armor: 0.35, heavy: 1.4, hq: 0.6 } },
  rocket:    { name: "Rockets",   icon: "🚀", cost: 100, hp: 120, dmg: 42, range: 2.4, rate: 1.4, speed: 0.95, cls: "soft",  needs: "barracks", count: 3, vs: { soft: 0.45, armor: 2.2, heavy: 1.0, hq: 1.0 } },
  tank:      { name: "Tank",      icon: "🛡️", cost: 160, hp: 520, dmg: 46, range: 1.8, rate: 1.2, speed: 0.75, cls: "armor", needs: "factory",  count: 1, vs: { soft: 1.8, armor: 0.8, heavy: 1.7, hq: 1.2 } },
  artillery: { name: "Artillery", icon: "💥", cost: 180, hp: 170, dmg: 70, range: 4.2, rate: 2.6, speed: 0.55, cls: "heavy", needs: "techlab",  count: 1, splash: 0.9, minRange: 1.6, vs: { soft: 1.6, armor: 0.5, heavy: 1.0, hq: 1.8 } },
};
export const BUILDINGS = {
  factory: { name: "Factory",  icon: "🏭", cost: 220, needs: "barracks", unlocks: "Tank" },
  techlab: { name: "Tech Lab", icon: "🔬", cost: 260, needs: "factory",  unlocks: "Artillery + faster strike" },
};
export const HARVESTER = { cost: 140, max: 3, rate: 2.2 }; // credits/sec each
export const BASE_INCOME = 4.5;
export const STRIKE = { dmg: 260, radius: 1.6, delay: 1.2, cooldown: 32, cooldownTech: 22 };
export const ORDERS = ["push", "hold", "focus"];

export function rng(seed = 1) {
  let s = seed >>> 0 || 1;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

function side() {
  return { credits: 200, harvesters: 1, built: { barracks: true, factory: false, techlab: false },
    hq: HQ_HP, orders: ["push", "push", "push"], strikeCd: 12, spent: 0 };
}

export function createBattle(seed = 1) {
  return { t: 0, seed, rand: rng(seed), sides: [side(), side()], units: [], strikes: [], events: [], nextId: 1, winner: null, firstContact: null };
}

export const dirOf = (o) => (o === 0 ? -1 : 1); // player 0 marches up (y decreasing)
export const ownHalf = (o, y) => (o === 0 ? y >= MID + 0.5 && y <= H - 0.3 : y <= MID - 0.5 && y >= 0.3);
export const nearestLane = (x) => LANES.reduce((b, lx, i) => (Math.abs(lx - x) < Math.abs(LANES[b] - x) ? i : b), 0);
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

export function canDeploy(b, o, type) {
  const u = UNITS[type], s = b.sides[o];
  if (!u || b.winner != null) return false;
  return s.built[u.needs] && s.credits >= u.cost;
}

export function deploy(b, o, type, lane, y) {
  if (!canDeploy(b, o, type)) return null;
  const u = UNITS[type], s = b.sides[o];
  if (y == null || !ownHalf(o, y)) y = o === 0 ? H - 2.6 : 2.6;
  s.credits -= u.cost; s.spent += u.cost;
  const squad = b.nextId;
  const out = [];
  for (let i = 0; i < u.count; i++) {
    const off = (i - (u.count - 1) / 2) * 0.42;
    const unit = { id: b.nextId++, squad, owner: o, type, lane, x: LANES[lane] + off, y: y + (i % 2) * 0.25 * -dirOf(o),
      hp: u.hp, max: u.hp, cd: 0.3 + i * 0.1, target: null, flash: 0 };
    b.units.push(unit); out.push(unit);
  }
  b.events.push({ k: "deploy", o, type, lane, x: LANES[lane], y });
  return out;
}

export function build(b, o, key) {
  const def = BUILDINGS[key], s = b.sides[o];
  if (!def || s.built[key] || !s.built[def.needs] || s.credits < def.cost) return false;
  s.credits -= def.cost; s.spent += def.cost; s.built[key] = true;
  b.events.push({ k: "build", o, key });
  return true;
}

export function buyHarvester(b, o) {
  const s = b.sides[o];
  if (s.harvesters >= HARVESTER.max || s.credits < HARVESTER.cost) return false;
  s.credits -= HARVESTER.cost; s.spent += HARVESTER.cost; s.harvesters++;
  b.events.push({ k: "harvester", o });
  return true;
}

export function setOrder(b, o, lane, order) {
  if (!ORDERS.includes(order) || lane < 0 || lane > 2) return false;
  b.sides[o].orders[lane] = order;
  return true;
}

export function strikeCooldown(s) { return s.built.techlab ? STRIKE.cooldownTech : STRIKE.cooldown; }

export function airstrike(b, o, x, y) {
  const s = b.sides[o];
  if (s.strikeCd > 0 || b.winner != null) return false;
  s.strikeCd = strikeCooldown(s);
  b.strikes.push({ o, x, y, t: STRIKE.delay });
  b.events.push({ k: "strikeCall", o, x, y });
  return true;
}

export const income = (s) => BASE_INCOME + s.harvesters * HARVESTER.rate;

function hurt(b, target, amount, from) {
  if (target.hq) {
    const s = b.sides[target.owner];
    s.hq = Math.max(0, s.hq - amount);
    b.events.push({ k: "hit", hq: true, o: target.owner, x: HQ[target.owner].x, y: HQ[target.owner].y, amount: Math.round(amount) });
    if (s.hq <= 0 && b.winner == null) { b.winner = 1 - target.owner; b.events.push({ k: "end", winner: b.winner }); }
    return;
  }
  target.hp -= amount; target.flash = 0.15;
  b.events.push({ k: "hit", id: target.id, x: target.x, y: target.y, amount: Math.round(amount), from });
}

function hqTarget(o) { return { hq: true, owner: o, x: HQ[o].x, y: HQ[o].y, cls: "hq" }; }

function pickTarget(b, u, def) {
  const order = b.sides[u.owner].orders[u.lane];
  const sight = Math.max(def.range + 1.2, 3);
  let best = null, score = Infinity;
  for (const e of b.units) {
    if (e.owner === u.owner || e.hp <= 0) continue;
    const d = dist(u, e);
    if (d > sight || (def.minRange && d < def.minRange)) continue;
    // Focus Fire: gang up on the weakest target in reach; otherwise prefer the best counter, then nearest.
    const mult = def.vs[UNITS[e.type].cls];
    const sc = order === "focus" ? e.hp / e.max * 4 + d * 0.3 : d - mult * 0.6;
    if (sc < score) { score = sc; best = e; }
  }
  if (best) return best;
  const enemyHq = hqTarget(1 - u.owner);
  if (dist(u, enemyHq) <= def.range + 0.9) return enemyHq;
  return null;
}

export function step(b, dt = DT) {
  if (b.winner != null) return b;
  b.t += dt;
  for (const s of b.sides) { s.credits += income(s) * dt; s.strikeCd = Math.max(0, s.strikeCd - dt); }

  for (const u of b.units) {
    const def = UNITS[u.type];
    u.flash = Math.max(0, u.flash - dt);
    u.cd -= dt;
    const tgt = pickTarget(b, u, def);
    u.target = tgt ? (tgt.hq ? "hq" : tgt.id) : null;
    const d = tgt ? dist(u, tgt) - (tgt.hq ? 0.9 : 0) : Infinity;
    if (tgt && d <= def.range) {
      if (!tgt.hq && b.firstContact == null) b.firstContact = b.t;
      if (u.cd <= 0) {
        u.cd = def.rate;
        const mult = def.vs[tgt.hq ? "hq" : UNITS[tgt.type].cls];
        const amount = def.dmg * mult * dmgScale(b.t);
        b.events.push({ k: "shot", o: u.owner, type: u.type, x: u.x, y: u.y, tx: tgt.x, ty: tgt.y });
        if (def.splash && !tgt.hq) {
          for (const e of b.units) if (e.owner !== u.owner && e.hp > 0 && dist(e, tgt) <= def.splash) hurt(b, e, e === tgt ? amount : amount * 0.5, u.type);
        } else hurt(b, tgt, amount, u.type);
      }
      continue;
    }
    // Move. Hold = stop just short of the river on your side. Chase a target that is in sight.
    const order = b.sides[u.owner].orders[u.lane];
    const holdY = u.owner === 0 ? MID + 1.2 : MID - 1.2;
    const sp = def.speed * dt;
    if (tgt) {
      const dx = tgt.x - u.x, dy = tgt.y - u.y, m = Math.hypot(dx, dy) || 1;
      if (order === "hold" && (u.owner === 0 ? u.y + (dy / m) * sp < holdY - 1.5 : u.y + (dy / m) * sp > holdY + 1.5)) continue;
      u.x += (dx / m) * sp; u.y += (dy / m) * sp;
    } else {
      if (order === "hold" && (u.owner === 0 ? u.y <= holdY : u.y >= holdY)) { u.x += (LANES[u.lane] - u.x) * 0.05; continue; }
      const goal = HQ[1 - u.owner];
      const nearHq = Math.abs(u.y - goal.y) < 4;
      const gx = nearHq ? goal.x : LANES[u.lane];
      const dx = (gx - u.x) * 0.15, dy = dirOf(u.owner);
      const m = Math.hypot(dx, dy) || 1;
      u.x += (dx / m) * sp; u.y += (dy / m) * sp;
    }
  }

  // HQ guns: each HQ shoots the nearest intruder.
  for (const o of [0, 1]) {
    const s = b.sides[o];
    s.hqCd = (s.hqCd ?? 0) - dt;
    if (s.hqCd > 0) continue;
    const hq = HQ[o];
    let best = null, bd = 3.6;
    for (const e of b.units) if (e.owner !== o && e.hp > 0) { const d = dist(e, hq); if (d < bd) { bd = d; best = e; } }
    if (best) { s.hqCd = 1.1; b.events.push({ k: "shot", o, type: "hq", x: hq.x, y: hq.y, tx: best.x, ty: best.y }); hurt(b, best, 38, "hq"); }
  }

  for (const st of b.strikes) {
    st.t -= dt;
    if (st.t <= 0 && !st.done) {
      st.done = true;
      b.events.push({ k: "strike", o: st.o, x: st.x, y: st.y });
      for (const e of b.units) if (e.owner !== st.o && e.hp > 0 && dist(e, st) <= STRIKE.radius) hurt(b, e, STRIKE.dmg, "strike");
      const ehq = hqTarget(1 - st.o);
      if (dist(ehq, st) <= STRIKE.radius) hurt(b, ehq, STRIKE.dmg * 0.25, "strike");
    }
  }
  b.strikes = b.strikes.filter((s) => !s.done);

  for (const u of b.units) if (u.hp <= 0 && !u.dead) { u.dead = true; b.events.push({ k: "ko", o: u.owner, type: u.type, x: u.x, y: u.y }); }
  b.units = b.units.filter((u) => !u.dead);
  return b;
}

export function drainEvents(b) { const e = b.events; b.events = []; return e; }

// ---------- AI general: same API, same economy, no cheats. ----------
export function createAI(o = 1, level = 1) { return { o, level, think: 1.5, plan: null }; }

function laneThreat(b, o) {
  const t = [0, 0, 0], mine = [0, 0, 0], armor = [0, 0, 0], soft = [0, 0, 0];
  for (const u of b.units) {
    const v = UNITS[u.type].cost / UNITS[u.type].count;
    if (u.owner === o) mine[u.lane] += v;
    else { t[u.lane] += v; if (UNITS[u.type].cls === "armor") armor[u.lane] += v; else soft[u.lane] += v; }
  }
  return { t, mine, armor, soft };
}

export function aiStep(b, ai, dt = DT) {
  if (b.winner != null) return;
  ai.think -= dt;
  if (ai.think > 0) return;
  ai.think = 1.0 + b.rand() * 0.8;
  const o = ai.o, s = b.sides[o];
  const { t, mine, armor, soft } = laneThreat(b, o);

  // Airstrike the fattest enemy clump.
  if (s.strikeCd <= 0) {
    let best = null, bv = 0;
    for (const u of b.units) {
      if (u.owner === o) continue;
      let v = 0;
      for (const e of b.units) if (e.owner !== o && dist(e, u) <= STRIKE.radius) v += UNITS[e.type].cost / UNITS[e.type].count;
      if (v > bv) { bv = v; best = u; }
    }
    if (best && bv >= 150) airstrike(b, o, best.x, best.y);
  }

  // Economy: save up for harvesters/buildings on a timeline unless a lane is in real danger.
  const danger = Math.max(...t.map((v, i) => v - mine[i]));
  const goal = s.harvesters < 2 && b.t > 8 ? ["h", HARVESTER.cost]
    : !s.built.factory && b.t > 25 ? ["factory", BUILDINGS.factory.cost]
    : s.harvesters < 3 && b.t > 50 ? ["h", HARVESTER.cost]
    : s.built.factory && !s.built.techlab && b.t > 75 ? ["techlab", BUILDINGS.techlab.cost] : null;
  if (goal) {
    if (s.credits >= goal[1]) { if (goal[0] === "h") buyHarvester(b, o); else build(b, o, goal[0]); return; }
    if (danger < 150) return;
  }

  // Defend the most threatened lane with the right counter, else push the weakest enemy lane.
  let lane = 0, worst = -Infinity;
  for (let i = 0; i < 3; i++) { const g = t[i] - mine[i]; if (g > worst) { worst = g; lane = i; } }
  if (worst <= 0) { lane = [0, 1, 2].sort((a, c2) => t[a] - t[c2] || mine[c2] - mine[a])[0]; }
  let pick;
  if (armor[lane] > soft[lane] && armor[lane] > 0) pick = "rocket";
  else if (soft[lane] > 200 && s.built.techlab) pick = "artillery";
  else if (soft[lane] > 0 && s.built.factory) pick = "tank";
  else pick = ["infantry", "rocket", "tank", "artillery"].filter((k) => s.built[UNITS[k].needs])[Math.floor(b.rand() * 4) % [0, 1, 2, 3].filter((i) => s.built[UNITS[["infantry", "rocket", "tank", "artillery"][i]].needs]).length];
  if (canDeploy(b, o, pick)) deploy(b, o, pick, lane);
  else if (worst > 120 && canDeploy(b, o, "infantry")) deploy(b, o, "infantry", lane);

  // Orders: push lanes we are winning, hold lanes we are losing, focus when even.
  for (let i = 0; i < 3; i++) {
    const r = mine[i] - t[i];
    setOrder(b, o, i, r > 150 ? "push" : r < -150 ? "hold" : mine[i] > 0 && t[i] > 0 ? "focus" : "push");
  }
}
